import { useNavigate, useOutletContext } from 'react-router-dom';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useFormik } from 'formik';
import { useQueryClient } from '@tanstack/react-query';

import { addKiosk } from 'api/Kiosk';

import { ia } from 'lib/helpers/utility';
import { useMember } from 'lib/hooks/queries/member/useMember';
import { useKioskHealthFor } from 'lib/hooks/useKioskHealthFor';
import { cancelReEmitFor, socketEmit, socketOff, socketOn } from 'lib/clients/socket';

import Hr from 'components/shared/Hr/Hr';
import Modal from 'components/shared/Modal/Modal';
import Button from 'components/shared/Button/Button';
import { showAlert } from 'components/shared/Alert/Alert';
import Allowed from 'components/shared/Permissions/Allowed';
import PhysicalAssetTable from 'components/Kiosk/components/PhysicalAssetTable';
import KioskBatteryStatus from 'components/shared/KioskBatteryStatus/KioskBatteryStatus';
import KioskTerminalSelect from 'components/Navbar/components/QuickActions/components/Kiosk/components/KioskTerminalSelect';

import EditKiosk from './EditKiosk';
import DebugActions from './components/DebugActions';
import KioskSetupActions from './components/KioskSetupActions';
import TerminalDeviceInfo from './components/TerminalDeviceInfo/TerminalDeviceInfo';
import { editKioskValidationSchema, getEditKioskInitialValues } from './lib/editKioskForm';

export default function EditKioskModal({ kiosk, setKiosk, practiceId, isOpen }) {
  const debugActionsRef = useRef();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const outletContext = useOutletContext();
  const [terminalSelect, setTerminalSelect] = useState(null);
  const [terminalInfo, setTerminalInfo] = useState(null);

  const isModalOpen = isOpen ?? !!kiosk;

  // Populate live kiosk/terminal battery for this kiosk directly, so it works in
  // the admin portal too (the sidebar useKioskHealthMonitor only runs in the
  // practice portal, leaving KioskBatteryStatus greyed out otherwise).
  useKioskHealthFor(isModalOpen ? kiosk : null);

  const handleKioskInfoUpdate = useCallback(
    (data) => {
      if (data?.kioskId && kiosk?.id && data.kioskId !== kiosk.id) return;
      if (ia(data?.devices)) {
        setTerminalSelect(data);
        return;
      }
      const next = data?.deviceInfo || data?.data || data?.payload || data;
      if (next && typeof next === 'object') {
        setTerminalInfo((prev) => ({ ...(prev || {}), ...next }));
      }
    },
    [kiosk?.id]
  );

  useEffect(() => {
    if (!isModalOpen || !kiosk?.id) return undefined;
    const joinPayload = { kioskId: kiosk.id };
    socketEmit('kiosk-room-join', joinPayload, { reemitOnReconnect: true });
    socketOn('kiosk-info-update', handleKioskInfoUpdate);
    return () => {
      socketOff('kiosk-info-update', handleKioskInfoUpdate);
      cancelReEmitFor('kiosk-room-join', joinPayload);
      socketEmit('kiosk-room-leave', { kioskId: kiosk.id });
    };
  }, [isModalOpen, kiosk?.id, handleKioskInfoUpdate]);

  useEffect(() => {
    setTerminalInfo(null);
    setTerminalSelect(null);
  }, [kiosk?.id]);

  const { data } = useMember({ params: { practiceId }, options: { enabled: isModalOpen } });
  const members = data?.members?.map((p) => ({
    value: p?.user_id,
    label: `${p?.f_name} ${p?.l_name}`
  }));

  const formik = useFormik({
    initialValues: getEditKioskInitialValues({ kiosk, members }),
    enableReinitialize: true,
    validationSchema: editKioskValidationSchema
  });

  useEffect(() => {
    if (isModalOpen) formik.resetForm();
  }, [isModalOpen]);

  const closeKioskModal = () => {
    setKiosk(null);
  };

  const addNewKiosk = async () => {
    const editedKiosk = await addKiosk(navigate, {
      practiceId: outletContext?.practiceId,
      values: {
        ...formik.values,
        notify_users: formik?.values?.notify_users?.map((np) => np.value)
      },
      kioskId: kiosk?.id
    });
    closeKioskModal();
    if (editedKiosk.code == 0) {
      showAlert({
        title: 'Kiosk updated',
        color: 'success',
        variant: 'lighter'
      });
    } else {
      showAlert({ title: "Couldn't update kiosk", color: 'danger' });
    }
    formik.resetForm();
    queryClient.invalidateQueries(['kiosks']);
  };

  return (
    <Modal
      title="Edit kiosk"
      isOpen={isModalOpen}
      handleClose={closeKioskModal}
      width="medium"
      bodyClassName="!overflow-y-auto"
      footer={
        <div className="flex w-full justify-end gap-4">
          <Button text="Cancel" variant="tertiary" onClick={closeKioskModal} size="small" />
          <Button
            text="Save changes"
            onClick={addNewKiosk}
            isDisabled={!formik.isValid || !formik.dirty}
            color="primary"
            size="small"
          />
        </div>
      }>
      <div style={{ paddingBottom: debugActionsRef?.current?.offsetHeight || 210 }}>
        <EditKiosk
          practitioners={data?.members?.map(({ user_id, f_name, l_name }) => ({
            value: user_id,
            label: `${f_name} ${l_name}`
          }))}
          formik={formik}
        />
        <Hr />
        <div className="-px-5 mb-4 flex-1">
          <div className="mb-3 flex flex-col gap-1">
            <p className="font-500 text-neutral-900">Battery</p>
            <p className="text-sm text-neutral-700">
              Live battery levels for the kiosk and its payment terminal.
            </p>
          </div>
          <KioskBatteryStatus kioskId={kiosk?.id} showLabels size={34} className="max-w-[260px]" />
        </div>
        <Allowed requiredPermissions="is_admin">
          <Hr />

          <div className="-px-5 mb-4 flex-1">
            <div className="mb-4 flex flex-col gap-1">
              <p className="font-500 text-neutral-900">Physical assets</p>
              <p className="text-sm text-neutral-700">
                Manage your basic details to keep your profile accurate and up to date.
              </p>
            </div>
            <PhysicalAssetTable kioskId={kiosk?.id} />
          </div>
        </Allowed>
        <Allowed requiredPermissions="is_admin">
          <Hr />
          <div className="-px-5 flex-1">
            <KioskSetupActions kiosk={kiosk} />
          </div>
        </Allowed>
      </div>
      <Allowed requiredPermissions="is_admin">
        <div
          ref={debugActionsRef}
          className="-px-5 bottom-14 left-0 h-fit w-full border-b-0 border-l-0 border-r-0 border-t-2 border-dashed border-neutral-300 bg-neutral-50 py-6">
          <DebugActions kiosk={kiosk} />
        </div>
      </Allowed>
      <Allowed requiredPermissions="kiosk.terminal_info">
        <Hr className="my-4" />
        <TerminalDeviceInfo kioskId={isModalOpen ? kiosk?.id : null} info={terminalInfo} />
      </Allowed>
      <KioskTerminalSelect
        data={terminalSelect}
        kioskName={kiosk?.name}
        onClose={() => setTerminalSelect(null)}
      />
    </Modal>
  );
}
